import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { AuthContext } from "@modules/auth/contexts/auth-plus.context";
import { TrainerPackageModel } from "../models/TrainerPackageModel";
import { PackageService } from "../services/TrainerPackageVerificationService";
import { fetchWithAuth } from "@modules/shared/helpers/fetch-with-auth.helper";
import { API_HEADER, BASE_URL } from "@modules/shared/enums/common-enum";

export const useTrainerPackageData = (
  filters: any,
  page: number,
  limit: number,
) => {
  const { auth, setAuth, logout } = useContext(AuthContext);
  const [packages, setPackages] = useState<TrainerPackageModel[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const trainerCache = useRef<Map<string, { name: string; avatar?: string }>>(
    new Map(),
  );
  const loadingRef = useRef(false);
  const authRef = useRef({ auth, setAuth, logout });
  authRef.current = { auth, setAuth, logout };

  const filtersKey = JSON.stringify(filters);

  const getTrainerInfo = useCallback(async (trainerId: string) => {
    if (trainerCache.current.has(trainerId)) {
      return trainerCache.current.get(trainerId)!;
    }
    const { auth, setAuth, logout } = authRef.current;
    try {
      const res = await fetchWithAuth(
        `${BASE_URL}/users/${trainerId}`,
        {
          method: "GET",
          headers: { ...API_HEADER, "x-platform": "web" },
        },
        auth,
        setAuth,
        logout,
      );
      if (res?.data) {
        const name = res.data.name || "";
        const avatar = res.data.avatars?.[0]?.url || "";
        const info = { name, avatar };
        trainerCache.current.set(trainerId, info);
        return info;
      }
    } catch (err) {
      console.warn(`Failed to fetch trainer ${trainerId}:`, err);
    }
    return null;
  }, []);

  const fetchPackages = useCallback(async () => {
    const { auth, setAuth, logout } = authRef.current;
    if (!auth || loadingRef.current) {
      if (!auth) setLoading(false);
      return;
    }
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const parsedFilters = JSON.parse(filtersKey);
      const res = await PackageService.getPendingPackages(
        auth,
        setAuth,
        logout,
        page,
        limit,
        parsedFilters,
      );
      if (!res) {
        setPackages([]);
        setTotal(0);
        return;
      }

      const packagesWithTrainer = await Promise.all(
        res.data.map(async (pkg) => {
          let trainerName = pkg.trainer_name || "";
          let trainerAvatar = "";
          if (pkg.trainer_id) {
            const info = await getTrainerInfo(pkg.trainer_id);
            if (info) {
              trainerName = info.name || trainerName;
              trainerAvatar = info.avatar || "";
            }
          }
          return {
            ...pkg,
            trainer_name: trainerName,
            trainer_avatar: trainerAvatar,
          };
        }),
      );

      setPackages(packagesWithTrainer);
      setTotal(res.pagination.total);
    } catch (err: any) {
      setError(err.message || "Failed to fetch packages");
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [page, limit, filtersKey, getTrainerInfo]);

  const isAuthed = !!auth;

  useEffect(() => {
    if (isAuthed) fetchPackages();
  }, [isAuthed, fetchPackages]);

  return { packages, total, loading, error, fetchPackages };
};
